import type { OpportunityFeedFilters, PaginatedRadarEvents, RadarEventFilters } from '~/features/opportunity-monitoring/types'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'

import { getRadarEventDetail, getRadarOpportunityDetail, listRadarEvents, listRadarLiveOddsEvidence, listRadarOpportunities, listWhaleOrders, type WhaleOrderFilters } from './api'

export const OPPORTUNITY_RADAR_REFETCH_INTERVAL_MS = 15_000
export const OPPORTUNITY_RADAR_STALE_TIME_MS = 30_000

export const opportunityRadarQueryKeys = {
  all: ['opportunity-radar'] as const,
  feed: (filters: OpportunityFeedFilters) => [...opportunityRadarQueryKeys.all, 'feed', filters] as const,
  events: (filters: RadarEventFilters) => [...opportunityRadarQueryKeys.all, 'events', filters] as const,
  eventDetail: (eventId: string) => [...opportunityRadarQueryKeys.all, 'event-detail', eventId] as const,
  detail: (opportunityId: string) => [...opportunityRadarQueryKeys.all, 'detail', opportunityId] as const,
  liveOddsEvidence: (input: { polymarketEventId: string, polymarketMarketId?: string, polymarketOutcomeId?: string }) =>
    [...opportunityRadarQueryKeys.all, 'live-odds-evidence', input] as const,
  whaleOrders: (filters?: WhaleOrderFilters) => [...opportunityRadarQueryKeys.all, 'whale-orders', filters ?? {}] as const,
}

export function radarEventsQueryOptions(filters: RadarEventFilters = { tab: 'matches', limit: 50 }) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.events(filters),
    queryFn: () => listRadarEvents(filters),
    refetchInterval: OPPORTUNITY_RADAR_REFETCH_INTERVAL_MS,
    staleTime: OPPORTUNITY_RADAR_STALE_TIME_MS,
  })
}

export function radarEventDetailQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.eventDetail(eventId),
    queryFn: () => getRadarEventDetail(eventId),
    enabled: Boolean(eventId),
    staleTime: OPPORTUNITY_RADAR_STALE_TIME_MS,
  })
}

export function radarOpportunitiesQueryOptions(filters: OpportunityFeedFilters = { limit: 50 }) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.feed(filters),
    queryFn: () => listRadarOpportunities(filters),
    refetchInterval: OPPORTUNITY_RADAR_REFETCH_INTERVAL_MS,
    staleTime: OPPORTUNITY_RADAR_STALE_TIME_MS,
  })
}

export function radarOpportunityDetailQueryOptions(opportunityId: string) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.detail(opportunityId),
    queryFn: () => getRadarOpportunityDetail(opportunityId),
    enabled: Boolean(opportunityId),
  })
}

export function radarLiveOddsEvidenceQueryOptions(input: {
  polymarketEventId: string
  polymarketMarketId?: string
  polymarketOutcomeId?: string
}) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.liveOddsEvidence(input),
    queryFn: () => listRadarLiveOddsEvidence(input),
  })
}

export function radarEventsInfiniteQueryOptions(
  filters: Omit<RadarEventFilters, 'cursor'> = { tab: 'matches', pageSize: 20 },
) {
  return infiniteQueryOptions({
    queryKey: [...opportunityRadarQueryKeys.all, 'events-infinite', filters] as const,
    queryFn: ({ pageParam }) =>
      listRadarEvents({ ...filters, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: PaginatedRadarEvents) => lastPage.nextCursor ?? undefined,
    refetchInterval: OPPORTUNITY_RADAR_REFETCH_INTERVAL_MS,
    staleTime: OPPORTUNITY_RADAR_STALE_TIME_MS,
  })
}

export function whaleOrdersQueryOptions(filters?: WhaleOrderFilters) {
  return queryOptions({
    queryKey: opportunityRadarQueryKeys.whaleOrders(filters),
    queryFn: () => listWhaleOrders(filters),
    refetchInterval: OPPORTUNITY_RADAR_REFETCH_INTERVAL_MS,
    staleTime: OPPORTUNITY_RADAR_STALE_TIME_MS,
  })
}
